import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Avatar, Box, Button, CircularProgress, Collapse, IconButton, Popover, Skeleton, Typography } from '@mui/material'
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CloseRounded from '@mui/icons-material/CloseRounded'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded'
import ErrorRounded from '@mui/icons-material/ErrorRounded'
import LockOutlined from '@mui/icons-material/LockOutlined'
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { toast } from 'react-toastify'
import {
  DESK_CALL_ROLES,
  describeDeskCallStatus,
  getDeskCall,
  placeDeskCall,
  previewDeskCall,
  startDeskCallRequest,
} from 'src/clinic/services/deskCallService'
import {
  getPortalVoiceFriendlyErrorMessage,
  subscribePortalVoiceCallState,
} from 'src/mentor/components/voice/portalVoiceCallManager'
import DeskCallerVerification from './DeskCallerVerification'

// Front desk → practitioner on duty. The desk never sees or picks a number: the server resolves who
// is checked in / rostered for the role and bridges the call. The popover shows who will be called
// (and who else could be), places the call, then follows the request to its outcome as a short
// timeline (ringing → answered, or no answer → messaged → on their way).

const OUTCOME_POLL_MS = 2500
const OUTCOME_POLL_MAX = 14
const ACTIVE_CALL_STATES = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending']

const TONES = {
  green: { fg: '#047857', bg: 'rgba(16, 185, 129, 0.12)', ring: '#10b981' },
  blue: { fg: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', ring: '#3b82f6' },
  amber: { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.14)', ring: '#f59e0b' },
  grey: { fg: '#475569', bg: 'rgba(148, 163, 184, 0.18)', ring: '#94a3b8' },
  red: { fg: '#b91c1c', bg: 'rgba(239, 68, 68, 0.1)', ring: '#ef4444' },
}

function candidateTone(candidate) {
  if (!candidate) return TONES.grey
  if (candidate.checkedIn) return TONES.green
  if (candidate.resolvedVia === 'rostered-now') return TONES.blue
  if (candidate.notYetOnDuty || candidate.resolvedVia === 'rostered-next') return TONES.amber
  return TONES.grey
}

function initialsOf(name) {
  return (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function formatClock(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function firstNameOf(candidate) {
  return candidate?.firstName || (candidate?.name || '').split(' ')[0] || ''
}

// The request's story so far, oldest first. Each step: { label, time, state: done|active|warn|error }.
function timelineOf(request) {
  if (!request) return []
  const name = request.targetDisplayName || 'The practitioner'
  const steps = [{ label: 'Request sent', time: request.createdAt, state: 'done' }]
  const status = request.status
  if (request.supportLine) {
    steps.push({ label: 'Nobody on duty · connecting to AUMHUM support', state: status === 'ringing' ? 'active' : 'done' })
  }
  if (status === 'resolving' || status === 'ringing') {
    steps.push({ label: `Ringing ${name}…`, state: 'active' })
    return steps
  }
  if (status === 'answered') {
    steps.push({ label: `${name} answered`, time: request.answeredAt, state: 'done' })
    return steps
  }
  if (status === 'nobody-on-duty') {
    steps.push({ label: request.outcomeDetail || 'Nobody is on duty', state: 'error' })
    return steps
  }
  if (status === 'failed') {
    steps.push({ label: 'The call could not connect', detail: request.outcomeDetail, state: 'error' })
    return steps
  }
  if (status === 'cancelled') {
    steps.push({ label: 'Cancelled before it rang through', state: 'warn' })
    return steps
  }
  if (['missed', 'notified', 'escalated', 'acknowledged', 'called-back', 'closed', 'expired'].includes(status)) {
    if (!request.answeredAt) {
      steps.push({ label: `${name} did not answer`, state: 'warn' })
    }
    if (request.practitionerWhatsappSent || request.notifiedAt) {
      steps.push({ label: 'Messaged on WhatsApp with a call-back link', time: request.notifiedAt, state: 'done' })
    }
    if (status === 'escalated') {
      steps.push({ label: request.outcomeDetail || 'Passed on to the next practitioner', state: 'active' })
    }
    if (request.acknowledgement || status === 'acknowledged') {
      steps.push({ label: `${name} is on the way`, time: request.acknowledgedAt, state: 'done' })
    }
    if (status === 'called-back') {
      steps.push({ label: `${name} called the desk back`, time: request.calledBackAt, state: 'done' })
    }
    if (status === 'closed') {
      steps.push({ label: `Closed: ${request.conclusionLabel || 'done'}`, time: request.concludedAt, state: 'done' })
    }
    if (status === 'missed' || status === 'notified') {
      steps.push({ label: 'Waiting for them to reply or call back', state: 'active' })
    }
  }
  return steps
}

function StepIcon({ state }) {
  if (state === 'done') return <CheckCircleRounded sx={{ fontSize: 18, color: TONES.green.ring }} />
  if (state === 'active') return <CircularProgress size={14} thickness={6} sx={{ m: '2px', color: TONES.blue.ring }} />
  if (state === 'warn') return <ErrorRounded sx={{ fontSize: 18, color: TONES.amber.ring }} />
  if (state === 'error') return <ErrorRounded sx={{ fontSize: 18, color: TONES.red.ring }} />
  return <RadioButtonUncheckedRounded sx={{ fontSize: 18, color: TONES.grey.ring }} />
}
StepIcon.propTypes = { state: PropTypes.string }

function CandidateRow({ candidate, selected, onSelect, disabled }) {
  const tone = candidateTone(candidate)
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) onSelect()
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1,
        borderRadius: '12px',
        cursor: disabled ? 'default' : 'pointer',
        border: '1.5px solid',
        borderColor: selected ? tone.ring : 'rgba(0,0,0,0.08)',
        backgroundColor: selected ? tone.bg : '#fff',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      }}
    >
      <Avatar sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 800, bgcolor: tone.bg, color: tone.fg, border: `2px solid ${tone.ring}` }}>
        {initialsOf(candidate?.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }} noWrap>
          {candidate?.name}
        </Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: tone.fg }} noWrap>
          {candidate?.statusLabel}
        </Typography>
      </Box>
      {selected ? <CheckCircleRounded sx={{ fontSize: 20, color: tone.ring }} /> : <RadioButtonUncheckedRounded sx={{ fontSize: 20, color: 'rgba(0,0,0,0.25)' }} />}
    </Box>
  )
}
CandidateRow.propTypes = { candidate: PropTypes.object, selected: PropTypes.bool, onSelect: PropTypes.func, disabled: PropTypes.bool }

export default function DeskCallButton({
  clinicBrandId,
  clinicId,
  role = 'screener',
  chamberRoomId,
  eventType,
  specialtySlug,
  doctorMentorId,
  bookedEventId,
  practitionerId,
  label,
  heading,
  reason,
  contextLine,
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  compact = false,
  sx,
  onRequestChange,
  // Optional custom trigger: ({ onClick, label, busy, request }) => element. Used by booking strips.
  renderTrigger,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [placing, setPlacing] = useState(false)
  const [activeRequest, setActiveRequest] = useState(null)
  const [callState, setCallState] = useState(null)
  const [triedPractitionerIds, setTriedPractitionerIds] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [showOwnPhone, setShowOwnPhone] = useState(false)
  const pollRef = useRef(null)
  const open = Boolean(anchorEl)
  const roleMeta = DESK_CALL_ROLES[role] || DESK_CALL_ROLES.screener
  const roleWord = roleMeta.label.toLowerCase()

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current)
    },
    [],
  )

  const loadPreview = useCallback(
    async (skipIds = []) => {
      if (!clinicId) return
      setPreviewLoading(true)
      const result = await previewDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        skipPractitionerIds: skipIds,
      })
      setPreview(result)
      setSelectedId(practitionerId || result?.primary?.practitionerId || null)
      setPreviewLoading(false)
    },
    [clinicBrandId, clinicId, role, chamberRoomId, eventType, specialtySlug, doctorMentorId, practitionerId],
  )

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    // Re-opening while a request is in flight keeps following it instead of starting over.
    if (!activeRequest || !['ringing', 'resolving', 'missed', 'notified', 'escalated'].includes(activeRequest.status)) {
      setActiveRequest(null)
      setTriedPractitionerIds([])
    }
    loadPreview([])
  }

  const handleClose = () => setAnchorEl(null)

  const trackRequest = useCallback(
    (request) => {
      setActiveRequest(request)
      onRequestChange?.(request)
    },
    [onRequestChange],
  )

  // After the softphone call ends, follow the request until the server settles it.
  const followOutcome = useCallback(
    (requestId) => {
      if (pollRef.current) clearInterval(pollRef.current)
      let attempts = 0
      pollRef.current = setInterval(async () => {
        attempts += 1
        const result = await getDeskCall(requestId)
        if (result?.ok && result?.deskCallRequest) {
          trackRequest(result.deskCallRequest)
          const settled = !['ringing', 'resolving'].includes(result.deskCallRequest.status)
          if (settled || attempts >= OUTCOME_POLL_MAX) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        } else if (attempts >= OUTCOME_POLL_MAX) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      }, OUTCOME_POLL_MS)
    },
    [trackRequest],
  )

  useEffect(() => {
    if (!activeRequest?.id) return
    if (callState?.status === 'ended' && ['ringing', 'resolving'].includes(activeRequest?.status)) {
      followOutcome(activeRequest.id)
    }
  }, [callState?.status, activeRequest?.id, activeRequest?.status, followOutcome])

  const candidates = useMemo(() => (Array.isArray(preview?.candidates) ? preview.candidates : []), [preview?.candidates])
  const primary = preview?.primary || null
  const selected = candidates.find((candidate) => candidate?.practitionerId === selectedId) || primary
  const nextCandidate = useMemo(
    () => candidates.find((candidate) => !triedPractitionerIds.includes(candidate?.practitionerId)) || null,
    [candidates, triedPractitionerIds],
  )

  const handleCall = async ({ skipIds = [], explicitPractitionerId = null, retryOfRequestId = null } = {}) => {
    setPlacing(true)
    try {
      const chosen = explicitPractitionerId || (selected && selected.practitionerId !== primary?.practitionerId ? selected.practitionerId : null)
      const result = await placeDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: chosen || practitionerId || undefined,
        skipPractitionerIds: skipIds,
        retryOfRequestId,
        reason,
      })
      if (result?.deskCallRequest) {
        trackRequest(result.deskCallRequest)
        if (result.deskCallRequest?.targetPractitionerId) {
          setTriedPractitionerIds((prev) => [...prev, result.deskCallRequest.targetPractitionerId])
        }
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || `No ${roleWord} is on duty right now.`)
      }
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the call right now.'))
    } finally {
      setPlacing(false)
    }
  }

  const handleNotifyOnly = async (targetPractitionerId = null) => {
    setPlacing(true)
    try {
      const result = await startDeskCallRequest({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: targetPractitionerId || practitionerId || undefined,
        skipPractitionerIds: targetPractitionerId ? [] : triedPractitionerIds,
        reason,
        notifyOnly: true,
      })
      if (!result?.ok) {
        toast.error(result?.message || 'Unable to send the WhatsApp message.')
        return
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || 'Nobody is on duty to message.')
        return
      }
      trackRequest(result?.deskCallRequest || null)
      toast.success(`${result?.deskCallRequest?.targetDisplayName || 'The practitioner'} has been messaged on WhatsApp.`)
    } finally {
      setPlacing(false)
    }
  }

  const routeDisabledReason = preview?.route && preview.route.enabled === false && primary ? preview.route.disabledReason : ''
  const blocker =
    preview?.deskCallEnabled === false
      ? 'Desk calling is switched off for this clinic.'
      : preview?.permissionEnabled === false
        ? `Calling the ${roleWord} on duty is not enabled for your account.`
        : routeDisabledReason
  const softphoneBusy = ACTIVE_CALL_STATES.includes(callState?.status)
  const requestInFlight = activeRequest && ['ringing', 'resolving'].includes(activeRequest.status)
  const requestMissed = ['missed', 'notified', 'escalated'].includes(activeRequest?.status)
  const nobodyNow = Boolean(preview?.ok && !primary)
  const nobodyToday = nobodyNow && !preview?.nextRosterStartAt
  const supportFallback = Boolean(preview?.supportLineFallback)
  const triggerLabel = label || `Call ${roleWord}`
  const timeline = timelineOf(activeRequest)
  const retryCandidate = requestMissed && nextCandidate && nextCandidate.practitionerId !== activeRequest?.targetPractitionerId ? nextCandidate : null

  const primaryAction = (() => {
    if (requestInFlight) return null
    if (retryCandidate) {
      return {
        label: `Try ${firstNameOf(retryCandidate)} next`,
        onClick: () => handleCall({ skipIds: triedPractitionerIds, explicitPractitionerId: retryCandidate.practitionerId, retryOfRequestId: activeRequest?.id || null }),
      }
    }
    if (nobodyNow) {
      return supportFallback && !blocker ? { label: 'Call AUMHUM support', support: true, onClick: () => handleCall({}) } : null
    }
    if (!selected) return null
    return {
      label: activeRequest ? `Call ${firstNameOf(selected)} again` : `Call ${firstNameOf(selected)}`,
      onClick: () => handleCall({ retryOfRequestId: activeRequest?.id || null }),
    }
  })()
  const whatsappTarget = retryCandidate || (nobodyNow ? null : selected)

  return (
    <>
      {typeof renderTrigger === 'function' ? (
        renderTrigger({ onClick: handleOpen, label: triggerLabel, busy: placing, request: activeRequest })
      ) : (
        <Button
          size={size}
          variant={variant}
          color="primary"
          fullWidth={fullWidth}
          startIcon={compact ? undefined : <PhoneInTalkRounded fontSize="small" />}
          onClick={handleOpen}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', ...sx }}
          aria-label={label || `Call ${roleMeta.article} on duty`}
        >
          {compact ? <PhoneInTalkRounded fontSize="small" /> : triggerLabel}
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 24px)', borderRadius: '16px', overflow: 'hidden' } } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, background: 'linear-gradient(135deg, rgba(13,148,136,0.10), rgba(59,130,246,0.06))' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>{heading || `Call the ${roleWord} on duty`}</Typography>
              {contextLine || reason ? (
                <Typography sx={{ fontSize: '12.5px', color: 'rgba(0,0,0,0.62)', mt: 0.25 }}>{contextLine || reason}</Typography>
              ) : null}
              {preview?.clinicLocationName ? (
                <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.45)', mt: 0.25 }}>{preview.clinicLocationName}</Typography>
              ) : null}
            </Box>
            <IconButton size="small" onClick={handleClose} aria-label="Close">
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {previewLoading && !preview ? (
            <Box>
              <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px', mb: 1 }} />
              <Skeleton variant="rounded" height={40} sx={{ borderRadius: '10px' }} />
            </Box>
          ) : preview && !preview.ok ? (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {preview.message}
            </Alert>
          ) : (
            <>
              {/* Who will be called */}
              {!activeRequest || !requestInFlight ? (
                nobodyNow ? (
                  <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: nobodyToday ? TONES.red.bg : TONES.amber.bg }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: nobodyToday ? TONES.red.fg : TONES.amber.fg }}>
                      {nobodyToday ? `No ${roleWord} available today` : `No ${roleWord} on duty right now`}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'rgba(0,0,0,0.65)', mt: 0.25 }}>
                      {nobodyToday
                        ? `Nobody is checked in or rostered as ${roleMeta.article} for the rest of today.`
                        : `${preview?.nextRosterPractitionerName || 'The next one'} is rostered from ${formatClock(preview?.nextRosterStartAt)}.`}{' '}
                      {supportFallback ? 'AUMHUM support can arrange one: call them below.' : 'Ask AUMHUM support to arrange one.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {candidates.slice(0, 4).map((candidate) => (
                      <CandidateRow
                        key={candidate.practitionerId}
                        candidate={candidate}
                        selected={candidate.practitionerId === selected?.practitionerId}
                        disabled={placing}
                        onSelect={() => setSelectedId(candidate.practitionerId)}
                      />
                    ))}
                    {candidates.length > 1 ? (
                      <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.5)' }}>
                        The first one is who the system would call. Tap another to call them instead.
                      </Typography>
                    ) : null}
                  </Box>
                )
              ) : null}

              {blocker ? (
                <Alert severity="warning" sx={{ mt: 1.25, borderRadius: '12px', fontSize: '12.5px' }}>
                  {blocker} Ask AUMHUM support to enable it.
                </Alert>
              ) : null}

              {/* What happened so far */}
              {timeline.length > 0 ? (
                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '12px', backgroundColor: 'rgba(15, 23, 42, 0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 800, color: 'rgba(0,0,0,0.55)', mb: 0.75 }}>
                    {activeRequest?.targetDisplayName ? `${activeRequest.targetDisplayName} · ${describeDeskCallStatus(activeRequest)}` : describeDeskCallStatus(activeRequest)}
                  </Typography>
                  {timeline.map((step, index) => (
                    <Box key={`${step.label}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.4 }}>
                      <Box sx={{ pt: '1px' }}>
                        <StepIcon state={step.state} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: step.state === 'active' ? 700 : 600 }}>{step.label}</Typography>
                        {step.detail ? <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.55)' }}>{step.detail}</Typography> : null}
                      </Box>
                      {step.time ? <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.45)', whiteSpace: 'nowrap' }}>{formatClock(step.time)}</Typography> : null}
                    </Box>
                  ))}
                  {requestInFlight ? (
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.5)', mt: 0.5 }}>Use the call panel to talk or hang up.</Typography>
                  ) : null}
                </Box>
              ) : null}

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
                {primaryAction ? (
                  <Button
                    variant="contained"
                    size="large"
                    disabled={placing || previewLoading || Boolean(blocker) || softphoneBusy}
                    startIcon={placing ? <CircularProgress size={16} color="inherit" /> : primaryAction.support ? <SupportAgentRounded /> : <PhoneInTalkRounded />}
                    onClick={primaryAction.onClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '15px',
                      borderRadius: '12px',
                      py: 1,
                      backgroundColor: primaryAction.support ? '#4338ca' : '#0f766e',
                      '&:hover': { backgroundColor: primaryAction.support ? '#3730a3' : '#115e59' },
                    }}
                  >
                    {primaryAction.label}
                  </Button>
                ) : null}
                {whatsappTarget && !requestInFlight ? (
                  <Button
                    variant="outlined"
                    size="medium"
                    disabled={placing || Boolean(blocker)}
                    startIcon={<WhatsAppIcon />}
                    onClick={() => handleNotifyOnly(whatsappTarget.practitionerId !== primary?.practitionerId || retryCandidate ? whatsappTarget.practitionerId : null)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#15803d', borderColor: 'rgba(21,128,61,0.4)' }}
                  >
                    WhatsApp {firstNameOf(whatsappTarget)} instead
                  </Button>
                ) : null}
              </Box>

              {/* Footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, color: 'rgba(0,0,0,0.5)' }}>
                <LockOutlined sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '11.5px' }}>
                  Numbers stay private. They hear &ldquo;call from the front desk&rdquo; and press 1 to accept.
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setShowOwnPhone((prev) => !prev)}
                endIcon={<ExpandMoreRounded sx={{ transform: showOwnPhone ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, mt: 0.5, px: 0.5, color: 'rgba(0,0,0,0.6)' }}
              >
                Calling from your own phone instead?
              </Button>
              <Collapse in={showOwnPhone} unmountOnExit>
                <DeskCallerVerification clinicId={clinicId} />
              </Collapse>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}

DeskCallButton.propTypes = {
  clinicBrandId: PropTypes.string,
  clinicId: PropTypes.string,
  role: PropTypes.oneOf(['screener', 'therapist', 'doctor']),
  chamberRoomId: PropTypes.string,
  eventType: PropTypes.string,
  specialtySlug: PropTypes.string,
  doctorMentorId: PropTypes.string,
  bookedEventId: PropTypes.string,
  practitionerId: PropTypes.string,
  label: PropTypes.string,
  heading: PropTypes.string,
  reason: PropTypes.string,
  contextLine: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  compact: PropTypes.bool,
  sx: PropTypes.object,
  onRequestChange: PropTypes.func,
  renderTrigger: PropTypes.func,
}
